import { centroid } from "@turf/turf";

// Relative tolerance under which two sides are considered parallel
const DEGENERATE_TOLERANCE = 1e-9;

// About a centimeter, in degrees
const RESET_TOLERANCE = 1e-7;

// Sign of the turn at each vertex, used to detect a polygon folded over itself
function turnSigns(points) {
    return points.map((point, index) => {
        const before = points[(index + points.length - 1) % points.length];
        const after = points[(index + 1) % points.length];
        const incoming = [point[0] - before[0], point[1] - before[1]];
        const outgoing = [after[0] - point[0], after[1] - point[1]];
        return Math.sign(incoming[0] * outgoing[1] - incoming[1] * outgoing[0]);
    });
}

function buildFeature(feature, coordinates) {
    return {
        ...feature,
        geometry: { ...feature.geometry, coordinates },
    };
}

// Homothety about `center`, for polygons without width/height axes
function scaleUniformly(feature, center, factor) {
    const coordinates = feature.geometry.coordinates.map((ring) =>
        ring.map(([lng, lat]) => [
            center[0] + (lng - center[0]) * factor,
            center[1] + (lat - center[1]) * factor,
        ])
    );
    return buildFeature(feature, coordinates);
}

// The quadrilateral in a local metric frame, or null if it has no usable axes
function toMetricQuadrilateral(feature, center) {
    const rings = feature.geometry.coordinates;
    const ring = rings[0];
    if (rings.length !== 1 || ring.length !== 5) {
        return null;
    }

    const lngCorrection = Math.cos((center[1] * Math.PI) / 180);
    const toMetric = ([lng, lat]) => [
        (lng - center[0]) * lngCorrection,
        lat - center[1],
    ];
    const toCoordinate = ([x, y]) => [
        center[0] + x / lngCorrection,
        center[1] + y,
    ];

    const vertices = ring.slice(0, -1).map(toMetric);
    const [p0, p1, , p3] = vertices;
    const u = [p1[0] - p0[0], p1[1] - p0[1]];
    const v = [p3[0] - p0[0], p3[1] - p0[1]];

    // Collinear or zero-length axes
    const determinant = u[0] * v[1] - u[1] * v[0];
    const magnitude = Math.hypot(u[0], u[1]) * Math.hypot(v[0], v[1]);
    if (Math.abs(determinant) <= DEGENERATE_TOLERANCE * magnitude) {
        return null;
    }

    const sides = vertices.map((start, index) => {
        const end = vertices[(index + 1) % vertices.length];
        const direction = [end[0] - start[0], end[1] - start[1]];
        return {
            start,
            end,
            direction,
            length: Math.hypot(direction[0], direction[1]),
        };
    });

    // Two consecutive sides parallel: no usable corner
    const hasUnusableCorner = sides.some((side, index) => {
        const previous = sides[(index + sides.length - 1) % sides.length];
        const cross =
            previous.direction[0] * side.direction[1] -
            previous.direction[1] * side.direction[0];
        return (
            Math.abs(cross) <=
            DEGENERATE_TOLERANCE * previous.length * side.length
        );
    });
    if (hasUnusableCorner) {
        return null;
    }

    return { toMetric, toCoordinate, vertices, sides, u, v, determinant };
}

// Whether the plot is scaled per axis rather than uniformly
export function isScalablePerAxis(feature) {
    return (
        toMetricQuadrilateral(
            feature,
            centroid(feature).geometry.coordinates
        ) != null
    );
}

/**
 * Scale a polygon along its own axes by sliding its sides, so that no angle
 * changes. Width is ring[0]->ring[1], height ring[0]->ring[3].
 * @param {object} feature GeoJSON Polygon feature
 * @param {number} widthFactor width multiplier (1 = as is)
 * @param {number} heightFactor height multiplier (1 = as is)
 * @returns {{feature: object, uniform: boolean}} the scaled feature, and
 *   whether it was scaled by the average factor instead
 */
export function scalePolygonPerAxis(feature, widthFactor, heightFactor) {
    const center = centroid(feature).geometry.coordinates;
    const ring = feature.geometry.coordinates[0];
    const quadrilateral = toMetricQuadrilateral(feature, center);

    if (!quadrilateral) {
        return {
            feature: scaleUniformly(
                feature,
                center,
                (widthFactor + heightFactor) / 2
            ),
            uniform: true,
        };
    }
    const { toMetric, toCoordinate, vertices, sides, u, v, determinant } =
        quadrilateral;

    // Per-axis affine map in the (u, v) basis
    const affine = ([x, y]) => {
        const a = ((x * v[1] - y * v[0]) / determinant) * widthFactor;
        const b = ((-x * u[1] + y * u[0]) / determinant) * heightFactor;
        return [a * u[0] + b * v[0], a * u[1] + b * v[1]];
    };

    // Slide each side along its normal by the affine offset of its middle
    const shiftedSides = sides.map(({ start, end, direction, length }) => {
        const normal = [direction[1] / length, -direction[0] / length];
        const middle = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
        const moved = affine(middle);
        const offset =
            (moved[0] - middle[0]) * normal[0] +
            (moved[1] - middle[1]) * normal[1];
        return {
            direction,
            point: [
                middle[0] + offset * normal[0],
                middle[1] + offset * normal[1],
            ],
        };
    });

    // Vertex i is the corner between side i-1 and side i
    const scaled = shiftedSides.map((side, index) => {
        const previous =
            shiftedSides[
                (index + shiftedSides.length - 1) % shiftedSides.length
            ];
        const cross =
            previous.direction[0] * side.direction[1] -
            previous.direction[1] * side.direction[0];
        const gap = [
            side.point[0] - previous.point[0],
            side.point[1] - previous.point[1],
        ];
        const along =
            (gap[0] * side.direction[1] - gap[1] * side.direction[0]) / cross;
        return [
            previous.point[0] + along * previous.direction[0],
            previous.point[1] + along * previous.direction[1],
        ];
    });

    // Folded over itself: fall back to the affine scale
    const foldsOver =
        !scaled.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y)) ||
        turnSigns(scaled).join() !== turnSigns(vertices).join();
    if (foldsOver) {
        return {
            feature: buildFeature(feature, [
                ring.map((coordinate) =>
                    toCoordinate(affine(toMetric(coordinate)))
                ),
            ]),
            uniform: false,
        };
    }

    // Put the polygon back on its center
    const drift = [
        scaled.reduce((sum, [x]) => sum + x, 0) / scaled.length,
        scaled.reduce((sum, [, y]) => sum + y, 0) / scaled.length,
    ];
    const centered = scaled.map(([x, y]) =>
        toCoordinate([x - drift[0], y - drift[1]])
    );

    return {
        feature: buildFeature(feature, [[...centered, centered[0]]]),
        uniform: false,
    };
}

function maxCoordinateGap(left, right) {
    return Math.max(
        ...left.flatMap((ring, ringIndex) =>
            ring.map((coordinate, index) => {
                const other = right[ringIndex]?.[index] ?? [Infinity, Infinity];
                return Math.max(
                    Math.abs(coordinate[0] - other[0]),
                    Math.abs(coordinate[1] - other[1])
                );
            })
        )
    );
}

// Whether `feature` is still `original` scaled by these factors
function isUntouchedScaleOf(feature, original, widthFactor, heightFactor) {
    if (
        !original ||
        original[0]?.length !== feature.geometry.coordinates[0].length
    ) {
        return false;
    }
    const { feature: rescaled } = scalePolygonPerAxis(
        buildFeature(feature, original),
        widthFactor,
        heightFactor
    );
    return (
        maxCoordinateGap(
            rescaled.geometry.coordinates,
            feature.geometry.coordinates
        ) <= RESET_TOLERANCE
    );
}

/**
 * Scale a plot from its geometry before the first scale when it has not been
 * moved since, so that successive scales chain exactly.
 * @param {object} feature the plot as it stands
 * @param {?object} recorded the scale already recorded for it
 * @param {number} widthFactor width multiplier (1 = as is)
 * @param {number} heightFactor height multiplier (1 = as is)
 * @returns {{feature: object, scale: object}} the scaled plot, and the scale
 *   to record
 */
export function scalePlot(feature, recorded, widthFactor, heightFactor) {
    const fromOriginal = isUntouchedScaleOf(
        feature,
        recorded?.original,
        (recorded?.width ?? 100) / 100,
        (recorded?.height ?? 100) / 100
    );
    const base = fromOriginal
        ? buildFeature(feature, recorded.original)
        : feature;
    const carriedWidth = fromOriginal ? recorded.width / 100 : 1;
    const carriedHeight = fromOriginal ? recorded.height / 100 : 1;

    const { feature: scaled, uniform } = scalePolygonPerAxis(
        base,
        carriedWidth * widthFactor,
        carriedHeight * heightFactor
    );

    // A uniform scale moves both accumulators together
    const applied = uniform ? (widthFactor + heightFactor) / 2 : null;
    let original;
    if (fromOriginal) {
        original = recorded.original;
    } else if (!recorded) {
        original = feature.geometry.coordinates;
    }

    return {
        feature: scaled,
        scale: {
            width: (recorded?.width ?? 100) * (applied ?? widthFactor),
            height: (recorded?.height ?? 100) * (applied ?? heightFactor),
            original,
        },
    };
}

/**
 * Undo the recorded scale: exact when the plot has not been moved since,
 * otherwise divides its current geometry by the factors.
 * @param {object} feature the scaled plot
 * @param {object} recorded the scale recorded for it
 * @returns {object} the unscaled plot
 */
export function unscalePlot(feature, recorded) {
    if (
        isUntouchedScaleOf(
            feature,
            recorded?.original,
            recorded.width / 100,
            recorded.height / 100
        )
    ) {
        return buildFeature(feature, recorded.original);
    }
    return scalePolygonPerAxis(
        feature,
        100 / recorded.width,
        100 / recorded.height
    ).feature;
}
